import { Eraser } from "@styled-icons/bootstrap";
import BlackAndRedSolidSvg from "assets/backgrounds/blackandredsolid.svg";
import BlackLinesSvg from "assets/backgrounds/BlankLines.svg";
import DoubleLineSvg from "assets/backgrounds/doubleLine.svg";
import GroundGrassSkySvg from "assets/backgrounds/groundGrassSky.svg";
import OneDottedSolidLineSvg from "assets/backgrounds/oneDottedSolidLIne.svg";
import OneSolidTwoDottedLineSvg from "assets/backgrounds/oneSolidTwoDottedLine.svg";
import SolidWithRedAndDottedLineSvg from "assets/backgrounds/Solid-with-Red-and-Dotted-Lines.svg";
import TwoSolidTwoDottedLineSvg from "assets/backgrounds/twoSolidTwoDotted.svg";
import { Move, Pencil, Reset } from "assets/icons/Common";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import CanvasDraw from "react-canvas-draw";
import styled from "styled-components";

import { MoveIconBtn } from "../FreeText/FreeText.styles";
import { FreeDrawOptionButton } from "./FreeHandWritingSettings";

export const CanvasScreen: React.FC = ({
  state,
  pixelPosition,
  settings,
  setState,
  onClickPicker,
}: any) => {
  const canvasRef = useRef<CanvasDraw | null>(null);
  const [canvaBrushRadius, setBrushRadius] = useState<number>(1); // Brush size state
  const [color, setColor] = useState<string>("#35363a");
  const [isEraserActive, setIsEraserActive] = useState(false);
  const [activeTool, setActiveTool] = useState<string>("write"); // Tracks the active tool

  useEffect(() => {
    setState({ ...settings?.canvas });
  }, [settings?.canvas?.selectedLine]);

  const handleClearCanvas = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
    setColor("#35363a");
    setActiveTool("");
  };

  const handleEraseSegments = () => {
    setIsEraserActive(!isEraserActive); // Toggle eraser mode
    setColor(isEraserActive ? "#35363a" : "transparent");
    setActiveTool("erase");
  };

  const handleBrushRadius = (size: number) => {
    setBrushRadius(size);
  };

  const handleWriteMode = () => {
    setColor("#35363a");
    setActiveTool("write");
  };

  const getBackgroundStyle = () => {
    switch (settings?.canvas?.selectedLine) {
      case "BlackAndRedSolidSvg":
        return BlackAndRedSolidSvg;
      case "TwoSolidTwoDottedSvg":
        return TwoSolidTwoDottedLineSvg;
      case "BlankLinesSvg":
        return BlackLinesSvg;
      case "DoubleLinesSvg":
        return DoubleLineSvg;
      case "GroundGrassSkySvg":
        return GroundGrassSkySvg;
      case "OneDottedSolidLineSvg":
        return OneDottedSolidLineSvg;
      case "OneSolidTwoDottedLinesSvg":
        return OneSolidTwoDottedLineSvg;
      case "SolidWithRedAndDottedLinesSvg":
        return SolidWithRedAndDottedLineSvg;
      default:
        return "transparent";
    }
  };

  // Function to handle redo
  const handleUndo = () => {
    if (canvasRef?.current) {
      canvasRef?.current?.undo();
    }
  };
  const handleRedo = () => {
    if (canvasRef?.current) {
      // canvasRef?.current?.undo();
    }
  };

  return (
    <>
      <OuterContainer width={pixelPosition?.width} height={pixelPosition?.height}>
        <Toolbar>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <StyledFreeDrawOptionButton onClick={handleUndo}>Undo</StyledFreeDrawOptionButton>

            {/* Redo Button */}
            {/* <StyledFreeDrawOptionButton onClick={handleRedo}>
            Redo
          </StyledFreeDrawOptionButton> */}
            {/* Write Mode Button */}
            <StyledFreeDrawOptionButton onClick={handleWriteMode} active={activeTool === "write"}>
              <Pencil />
            </StyledFreeDrawOptionButton>

            {/* Eraser Button */}
            {/* <StyledFreeDrawOptionButton
            onClick={handleEraseSegments}
            active={activeTool === "erase"}
          >
            <Eraser />
          </StyledFreeDrawOptionButton> */}

            {/* Color Picker */}
            <ColorPaletteIcon>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                disabled={isEraserActive} // Disable color picker in eraser mode
              />
            </ColorPaletteIcon>

            {/* Clear Canvas Button */}
            <StyledFreeDrawOptionButton onClick={handleClearCanvas}>
              <Reset style={{ transform: "rotateY(180deg)" }} />
            </StyledFreeDrawOptionButton>

            {/* Brush Size Slider */}
            <StyledFreeDrawOptionButton>
              <input
                type="range"
                min="1"
                max="5"
                value={canvaBrushRadius}
                onChange={(e) => handleBrushRadius(parseInt(e.target.value))}
              />
            </StyledFreeDrawOptionButton>
          </div>
        </Toolbar>

        <DraggableWrapper>
          {/* Background Image */}
          <BackgroundLayer
            width={pixelPosition?.width}
            height={pixelPosition?.height}
            bgImage={getBackgroundStyle()}
          />

          {/* Canvas Layer (Above the Background) */}
          <DrawingContainer width={pixelPosition?.width} height={pixelPosition?.height}>
            <CanvasDraw
              ref={canvasRef}
              brushColor={color}
              brushRadius={canvaBrushRadius}
              lazyRadius={0}
              hideGrid={true}
              canvasWidth={pixelPosition?.width ? pixelPosition?.width : 500}
              canvasHeight={pixelPosition?.height ? pixelPosition?.height : 500}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                background: "transparent",
                zIndex: 2,
              }}
            />
          </DrawingContainer>
        </DraggableWrapper>
      </OuterContainer>
      <MoveIconBtn Icon={Move} />
    </>
  );
};

const StyledFreeDrawOptionButton = styled(FreeDrawOptionButton)<{
  active?: boolean;
}>`
  background-color: ${({ active }) => (active ? "#ff8f71" : "transparent")};
  &:hover {
    background-color: #ff8f71;
  }
`;

const OuterContainer = styled.div<{
  width: number;
  height: number;
}>`
  position: relative;
  overflow: hidden;
`;

const DrawingContainer = styled.div<{
  width: number;
  height: number;
}>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  position: relative;
  z-index: 2; /* Ensure this is above the background */
  overflow: hidden;
`;

const BackgroundLayer = styled.div<{
  width: number;
  height: number;
  bgImage: string;
}>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  position: absolute;
  background-image: ${({ bgImage }) => `url(${bgImage})`};
  background-repeat: repeat;
  background-position: center;
  z-index: 1; /* Ensure this stays behind the canvas */
`;

const Toolbar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 11; /* Higher z-index to ensure it is above the drawing container */
  width: 100%;
  background: #f3f3f3;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  border-radius: 0.5rem;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1); /* Optional for better visibility */
`;

const DraggableWrapper = styled.div.attrs({
  className: classNames("no-drag"),
})``;

const ColorPaletteIcon = styled.label`
  margin-bottom: 0;
  input {
    width: 50px;
    height: 43px;
    border-radius: 8px;
    border-color: rgb(255, 143, 113);
    background-color: #fff;
  }
`;
